import { FC, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import CommentIcon from '@mui/icons-material/Comment';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { DateTime } from 'luxon';
import useInterval from 'use-interval';
import { api } from 'services/api';
import { getUserInfo } from 'redux/reducers/user/selectors';
import { getEMerchant } from 'redux/reducers/monthlyFee/selectors';
import { TEmCommentItem } from 'redux/reducers/emManagement/types';
import { TUser } from 'redux/reducers/user/types';
import { UserAvatar } from 'ui/UserAvatar';
import { ESortDirection } from 'ui/EasTable/types';
import { EasTooltip } from 'ui/EasTooltip';
import { INDICATOR_BADGE_SX } from '../constants';
import { Menu, MenuItem, Divider } from '@mui/material';

const REFRESH_INTERVAL = 60_000;
const COMMENTS_PAGE_SIZE = 120;

const getCreatedDate = (item: TEmCommentItem): string | null =>
	DateTime.fromISO(item.createdAt, { zone: 'utc' }).toLocal().toISODate();

export const LastCommentsIndicator: FC = () => {
	const userinfo = useSelector(getUserInfo);
	const userId = userinfo?.id;
	const emList = useSelector(getEMerchant);

	const [comments, setComments] = useState<TEmCommentItem[]>([]);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	const emNames = useMemo(() => {
		const names: Record<number, string> = {};
		emList?.forEach((item) => {
			names[item.id] = item.fullName;
		});
		return names;
	}, [emList]);

	const fetchComments = useCallback(() => {
		if (!userId) return;
		api.emManagement
			.getEmComments({
				pageNumber: 1,
				pageSize: COMMENTS_PAGE_SIZE,
				orderBy: [{ fieldName: 'id', direction: ESortDirection.DESC }],
				filters: [
					{ fieldName: 'scheduledActionOn', value: 'null', operator: '=' },
					{ fieldName: 'createdBy', value: userId, operator: '=' },
				],
			})
			.then((response) => {
				const items = (response?.data ?? []).filter(
					(item) =>
						!item.scheduledActionOn &&
						item.operatorsToNotify?.some((operator) => operator.id === userId),
				);
				setComments(items);
			})
			.catch(() => {
				// The indicator is non-critical; keep the last known state on failure.
			});
	}, [userId]);

	useEffect(() => {
		fetchComments();
	}, [fetchComments]);

	useInterval(() => {
		fetchComments();
	}, REFRESH_INTERVAL);

	const todayIso = DateTime.now().toISODate();
	const hasComments = comments.length > 0;
	const todayCount = comments.filter((item) => getCreatedDate(item) === todayIso).length;

	const handleOpen = (event: MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};

	const tooltipTitle =
		todayCount > 0
			? `${todayCount} comment${todayCount === 1 ? '' : 's'} today`
			: hasComments
				? 'Last comments'
				: 'No comments';

	return (
		<>
			<EasTooltip title={tooltipTitle}>
				<span>
					<Badge
						badgeContent={todayCount}
						color="error"
						invisible={todayCount === 0}
						sx={INDICATOR_BADGE_SX}
					>
						<IconButton
							color="primary"
							onClick={handleOpen}
							sx={{ p: 0.5 }}
							disabled={!hasComments}
						>
							<CommentIcon sx={{ fontSize: '1.6rem' }} />
						</IconButton>
					</Badge>
				</span>
			</EasTooltip>
			<Menu
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				<Box sx={{ p: 2, maxWidth: '700px', maxHeight: '420px', overflow: 'auto' }}>
					<Typography variant="body1" color="primary" fontWeight="bold">
						Last Comments
					</Typography>

					{comments.map((item) => {
						const isToday = getCreatedDate(item) === todayIso;
						return (
							<Box key={item.id}>
								<MenuItem>
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'column',
											justifyContent: 'start',
											alignItems: 'start',
											gap: 1,
										}}
									>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
												<Typography variant="body2" color="text.secondary">
													Notified to:
												</Typography>
												{item.operatorsToNotify.length > 0 &&
													item.operatorsToNotify.map((operator) => (
														<UserAvatar
															key={operator.id}
															user={operator as TUser}
															size="xxs"
															tooltip
														/>
													))}
											</Box>
										</Box>
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'space-between',
												gap: 1,
											}}
										>
											<Typography
												textAlign="left"
												sx={{ fontWeight: isToday ? 900 : 400 }}
												color="text.secondary"
												variant="body2"
												component={RouterLink}
												to={`/em-management/${item.emId}`}
												onClick={handleClose}
											>
												{emNames[item.emId]}
											</Typography>
										</Box>

										<Typography
											textAlign="left"
											sx={{
												fontWeight: isToday ? 700 : 400,
												maxWidth: '600px',
												whiteSpace: 'normal',
											}}
										>
											{item.comment}
										</Typography>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
												<Typography variant="body2" color="text.secondary">
													Created at:
												</Typography>
												<Typography
													textAlign="left"
													sx={{ fontWeight: 400 }}
													color="text.secondary"
													variant="body2"
												>
													{new Date(item.createdAt).toLocaleDateString()}{' '}
												</Typography>
											</Box>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
												<Typography variant="body2" color="text.secondary">
													Created by:
												</Typography>
												<UserAvatar user={item.createdBy as TUser} size="xxs" tooltip />
											</Box>
										</Box>

										{/* <Typography
											textAlign="left"
											sx={{
												fontWeight: isToday ? 700 : 400,
												width: '600px',
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
											}}
											variant="body2"
										>
											{item.createdBy.fullName}
										</Typography> */}
									</Box>
								</MenuItem>
								<Divider variant="middle" />
							</Box>
						);
					})}
				</Box>
			</Menu>
		</>
	);
};
